import type { ReactNode } from "react";
import { SkyCanvas } from "./SkyCanvas.tsx";

type Feature = {
  title: string;
  body: string;
  icon: ReactNode;
  delay: string;
  extra?: ReactNode;
};

const iconProps = {
  viewBox: "0 0 24 24",
  className: "size-5",
  "aria-hidden": true as const,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const features: Feature[] = [
  {
    title: "明确打断",
    body: "全屏倒计时让提醒不再被通知中心淹没，帮你真正从连续伏案里停下来。",
    delay: "0ms",
    icon: (
      <svg {...iconProps}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
  },
  {
    title: "克制配置",
    body: "只保留提醒间隔、工作时段、倒计时、文字和白名单，日常维护成本很低。",
    delay: "60ms",
    icon: (
      <svg {...iconProps}>
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
  {
    title: "场景安静",
    body: "白名单命中时自动跳过本次提醒，会议、演示和剪辑时不乱打断。",
    delay: "120ms",
    icon: (
      <svg {...iconProps}>
        <path d="M12 3a6 6 0 0 0-6 6v3l-2 3h16l-2-3V9a6 6 0 0 0-6-6z" />
        <path d="M10 20h4" />
      </svg>
    ),
    extra: (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
        <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <span className="size-2 rounded-full bg-status-busy" />
          会议中
        </span>
        <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <span className="size-2 rounded-full bg-status-done" />
          自动跳过
        </span>
        <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <span className="size-2 rounded-full bg-status-attention" />
          演示中
        </span>
      </div>
    ),
  },
  {
    title: "源码开放",
    body: "源码按 Apache-2.0 开放，想自构建、改公钥或换更新源都可以自己维护。",
    delay: "180ms",
    icon: (
      <svg {...iconProps}>
        <path d="M9 19c-4.3 1.4-4.3-2.5-6-3m12 5v-3.5c0-1 .1-1.4-.5-2 2.8-.3 5.5-1.4 5.5-6a4.6 4.6 0 0 0-1.3-3.2 4.2 4.2 0 0 0-.1-3.2s-1.1-.3-3.5 1.3a12.3 12.3 0 0 0-6.2 0C6.5 2.8 5.4 3.1 5.4 3.1a4.2 4.2 0 0 0-.1 3.2A4.6 4.6 0 0 0 4 9.5c0 4.6 2.7 5.7 5.5 6-.6.6-.6 1.2-.5 2V21" />
      </svg>
    ),
  },
  {
    title: "离线可靠",
    body: "官方版激活后核心提醒和授权校验都在本机完成，不依赖后台常在线。",
    delay: "240ms",
    icon: (
      <svg {...iconProps}>
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M8 21h8M12 18v3" />
        <path d="M7 11h.01M12 11h.01M17 11h.01" />
      </svg>
    ),
  },
  {
    title: "跨平台覆盖",
    body: "Windows、macOS、Linux 都能用，覆盖大多数桌面办公环境。",
    delay: "300ms",
    icon: (
      <svg {...iconProps}>
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M8 20h8M12 16v4" />
      </svg>
    ),
  },
];

export function Features() {
  return (
    <section id="features" className="relative isolate scroll-mt-24 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent, black 35%, black 55%, transparent 72%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 35%, black 55%, transparent 72%)",
        }}
      >
        <SkyCanvas mode="mist" className="absolute inset-x-0 top-0 h-[72%] w-full -scale-y-100" />
      </div>

      <div className="mx-auto w-full max-w-6xl px-6 pb-24 pt-10 sm:pb-32 sm:pt-14">
        <div
          data-reveal=""
          style={{ ["--reveal-delay" as string]: "0ms" }}
          className="mx-auto max-w-2xl text-center"
        >
          <h2 className="text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
            为什么它比普通通知更有效
          </h2>
        </div>

        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div
              key={f.title}
              data-reveal=""
              style={{ ["--reveal-delay" as string]: f.delay }}
              className="flex scroll-mt-24 flex-col gap-4 rounded-2xl border border-white/[0.08] bg-[oklch(0.17_0.005_285_/_0.92)] p-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] transition-colors hover:bg-[oklch(0.19_0.005_285_/_0.94)]"
            >
              <span className="flex size-10 items-center justify-center rounded-xl border border-border/60 bg-foreground/[0.03] text-foreground/80">
                {f.icon}
              </span>
              <h3 className="text-xl font-semibold tracking-tight">{f.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              {f.extra}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
